/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Object3D,
  Object3DEventMap,
  PickEventDetail,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import { TileMapModel } from '@technology/web3d-plugin-tilemap';
import {
  CADEntity,
  CADEventName,
  CADStyle,
  CompoundCurve,
  ICADObject,
  ICompoundCurve,
  IPoint,
  LineEditingAction,
  LineEditingModel,
  LineEditingPlugin,
} from '@technology/web3d-plugin-line-editing';
import { GridPlugin } from '@technology/web3d-plugin-grid';
import {
  type ITextObject,
  TEXT_CREATED_EVENT,
  TEXT_SELECTION_CHANGED_EVENT,
  TEXT_INSERTION_POINT_CHANGED_EVENT,
  TextEditingModel,
  TextEditingPlugin,
  type TextEntity,
  type TextDragConstraintSettings,
} from '@trimble/web3d-plugin-text-editing';
import { Accessor, batch, createSignal } from 'solid-js';
import { CapabilityCategory } from './base/models/capability-category';
import { SingleCapabilityBase } from './base/single-capability-base';
import { ThreejsModel, ThreejsPlugin } from '@technology/web3d-plugin-threejs';
import {
  Web3DMeshMaterial,
  Web3DMeshMaterialParameters,
} from '@technology/web3d/dist/esnext/Rendering/Web3DMeshMaterial';
import {
  LineSelectionSink,
  LineSelectionSourceId,
} from './line-selection-sink';
import { selectionStillInModel } from './selection-in-model';

/** Source id used for paths published by the data-prep capability. */
const DATA_PREP_SECTION_SOURCE = LineSelectionSourceId.dataPrep;
// eslint-disable-next-line @typescript-eslint/naming-convention
const DATA_PREP_MODEL_NAME = 'data-prep';
const DATA_PREP_TEXT_MODEL_NAME = 'data-prep-text';
const THREEJS_MODEL = 'threejs';
const POSITION_FLOATS_PER_VERTEX = 3;
const MIN_PATH_VERTICES = 2;

const DEFAULT_CAMERA_HEIGHT = 1000;
export interface LineRenderingSpecification {
  points: IPoint[];
  lines: ICompoundCurve[];
  texts?: ITextObject[];
  presentIds: number[];
  styles: Map<string, CADStyle>;
  /** GUIDs of objects we have sent to the server but not yet confirmed; keep these in view when applying presentIds. */
  pendingGuids?: ReadonlySet<string>;
  /**
   * Layer ids that are currently visible (local layer manager). Used when building the spec
   * from the line model; lines/points on the spec are applied in full to the plugin.
   */
  visibleLayerIds?: ReadonlySet<number>;
}

/**
 * The viewer keeps the ids it has selected per model and only revisits them on the next selection
 * change, so the ids of geometry another user just deleted are still listed. The plugin answers
 * those with a hole in the array (its return type says otherwise), and everything downstream reads
 * the entities, so the holes have to be dropped here.
 */
function resolveSelectedEntities(
  model: LineEditingModel | undefined,
  ids: number[]
): CADEntity[] {
  if (!model) {
    return [];
  }
  const resolved: (CADEntity | undefined)[] = model.getEntitiesByIds(...ids);
  return resolved.filter((entity): entity is CADEntity => entity !== undefined);
}

export class DataPrepCapability extends SingleCapabilityBase {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public readonly DEFAULT_CAMERA_HEIGHT = DEFAULT_CAMERA_HEIGHT;

  public readonly mapModels: Record<string, TileMapModel> = {};

  public override category: CapabilityCategory = CapabilityCategory.Markup;
  public lineEditingModel: LineEditingModel | undefined;
  public textEditingModel: TextEditingModel | undefined;
  public threeJsModel: ThreejsModel | undefined;
  private readonly readonlyLineEditingModels = new Map<
    string,
    LineEditingModel
  >();
  private readonly readonlySurfaceGuidsByModelSpace = new Map<
    string,
    ReadonlySet<string>
  >();
  /**
   * GUIDs last applied to each readonly line-editing model. Kept after a plugin
   * delete so ActionPerformed can still identify readonly ownership.
   */
  private readonly readonlyCadGuidsByModelSpace = new Map<
    string,
    ReadonlySet<string>
  >();
  /** Drafting space currently being edited; used to tag editable text ownership. */
  private editableModelSpaceId: string | undefined;
  private lineRenderingApplyQueue: Promise<void> = Promise.resolve();
  private lineSelectionSections: LineSelectionSink | undefined;
  private unregisterSectionRefresher: (() => void) | undefined;

  public readonly isConstrainDragZ: Accessor<boolean>;
  public readonly isConstrainDragToZAxis: Accessor<boolean>;
  private readonly setIsConstrainDragZ: (value: boolean) => void;
  private readonly setIsConstrainDragToZAxis: (value: boolean) => void;

  constructor(
    viewer: Web3DViewer,
    private readonly lineEditingPlugin: LineEditingPlugin,
    private readonly textEditingPlugin: TextEditingPlugin,
    private readonly gridPlugin: GridPlugin,
    private readonly threeJsPlugin: ThreejsPlugin,
    private onActionPerformed?: (action: LineEditingAction) => void,
    private onSelected?: (
      entities: CADEntity[],
      models: Object3D<Object3DEventMap>[],
      selectionIsReadonly?: boolean,
      textEntities?: TextEntity[]
    ) => void,
    private onTextInsertionPointChanged?: (detail: {
      guid: string;
      insertionPoint: { x: number; y: number; z: number };
    }) => void,
    private onTextCreated?: (text: ITextObject) => void
  ) {
    super(viewer);

    const [isConstrainDragZ, setIsConstrainDragZ] = createSignal(false);
    const [isConstrainDragToZAxis, setIsConstrainDragToZAxis] =
      createSignal(false);
    this.isConstrainDragZ = isConstrainDragZ;
    this.isConstrainDragToZAxis = isConstrainDragToZAxis;
    this.setIsConstrainDragZ = setIsConstrainDragZ;
    this.setIsConstrainDragToZAxis = setIsConstrainDragToZAxis;

    const existingThreeJsModel = this.viewer.getModel(THREEJS_MODEL);
    if (existingThreeJsModel) {
      this.threeJsModel = existingThreeJsModel as ThreejsModel;
    } else {
      this.threeJsModel = this.threeJsPlugin.createModel();
    }
    this.handlePick = this.handlePick.bind(this);
    this.handleTextSelection = this.handleTextSelection.bind(this);
    this.handleTextInsertionPointChanged =
      this.handleTextInsertionPointChanged.bind(this);
    this.handleTextCreated = this.handleTextCreated.bind(this);
  }

  /**
   * Wires this capability to a {@link LineSelectionSink}.
   * After this call, selecting a {@link CompoundCurve} publishes a path to the
   * sink (which in turn drives section generation when its toggle is on).
   * Calling again replaces any previously-attached sink.
   */
  public attachLineSelectionSections(sink: LineSelectionSink): void {
    this.unregisterSectionRefresher?.();
    this.lineSelectionSections = sink;
    this.unregisterSectionRefresher = sink.registerSource(
      DATA_PREP_SECTION_SOURCE,
      () => this.publishSelectedSectionPaths()
    );
  }

  public updateOnActionPerformed(
    onActionPerformed: (action: LineEditingAction) => void
  ): void {
    this.onActionPerformed = onActionPerformed;
  }

  public updateOnSelected(
    onSelected: (
      entities: CADEntity[],
      models: Object3D<Object3DEventMap>[],
      selectionIsReadonly?: boolean,
      textEntities?: TextEntity[]
    ) => void
  ): void {
    this.onSelected = onSelected;
  }

  public updateOnTextInsertionPointChanged(
    onTextInsertionPointChanged: (detail: {
      guid: string;
      insertionPoint: { x: number; y: number; z: number };
    }) => void
  ): void {
    this.onTextInsertionPointChanged = onTextInsertionPointChanged;
  }

  public updateOnTextCreated(onTextCreated: (text: ITextObject) => void): void {
    this.onTextCreated = onTextCreated;
  }

  public createMeshMaterial(
    parameters: Web3DMeshMaterialParameters
  ): Web3DMeshMaterial | undefined {
    return this.threeJsPlugin.createMeshMaterial(parameters);
  }

  /**
   * Cleanup the extension.
   * This will unload all models and detach from any line-selection sink so
   * the coordinator can release its reference to this capability.
   * @returns A promise that resolves when the cleanup is complete.
   * @throws Error if the active model is not initialized.
   */
  public async cleanup(): Promise<void> {
    this.detachLineSelectionSections();
    for (const modelSpaceId of [...this.readonlyLineEditingModels.keys()]) {
      await this.disposeReadonlyLineEditingModel(modelSpaceId);
    }
    if (this.textEditingModel) {
      this.viewer.removeEventListener(
        TEXT_SELECTION_CHANGED_EVENT,
        this.handleTextSelection
      );
      this.viewer.removeEventListener(
        TEXT_INSERTION_POINT_CHANGED_EVENT,
        this.handleTextInsertionPointChanged
      );
      this.textEditingModel.dispose();
      this.textEditingModel = undefined;
    }
    this.unload();
    await Promise.resolve();
  }

  /**
   * Reverses {@link attachLineSelectionSections}. Safe to call when no sink
   * is attached.
   */
  public detachLineSelectionSections(): void {
    this.unregisterSectionRefresher?.();
    this.unregisterSectionRefresher = undefined;
    this.lineSelectionSections = undefined;
  }
  public override enable(): Promise<void> {
    return Promise.resolve();
  }
  public initializeLineEditing(options?: { createGrid?: boolean }): void {
    if (options?.createGrid !== false) {
      this.gridPlugin.createGrid();
    }

    this.lineEditingModel = this.lineEditingPlugin.createModel({
      modelId: DATA_PREP_MODEL_NAME,
    });

    if (this.threeJsModel) {
      this.textEditingModel = this.textEditingPlugin.createModel({
        modelId: DATA_PREP_TEXT_MODEL_NAME,
        viewer: this.viewer,
        threejsModel: this.threeJsModel,
      });
      this.textEditingModel.setDragConstraintSettingsProvider(() =>
        this.getTextDragConstraintSettings()
      );
    }

    this.viewer.addEventListener(
      CADEventName.ActionPerformed,
      this.handleActionPerformed.bind(this)
    );

    this.viewer.addEventListener('selection', this.handleSelection.bind(this));
    this.viewer.addEventListener(
      TEXT_SELECTION_CHANGED_EVENT,
      this.handleTextSelection
    );
    this.viewer.addEventListener(
      TEXT_INSERTION_POINT_CHANGED_EVENT,
      this.handleTextInsertionPointChanged
    );
    this.viewer.addEventListener(TEXT_CREATED_EVENT, this.handleTextCreated);

    batch(() => {
      this.setIsConstrainDragZ(
        this.lineEditingPlugin.settings.constrainDragZ === true
      );
      this.setIsConstrainDragToZAxis(
        this.lineEditingPlugin.settings.constrainDragToZAxis === true
      );
    });
  }

  /** Constrains drag handles to the Z value from drag start; mutually exclusive with {@link setConstrainDragToZAxis}. */
  public setConstrainDragZ(enabled: boolean): void {
    this.lineEditingPlugin.settings.constrainDragZ = enabled;
    batch(() => {
      this.setIsConstrainDragZ(enabled);
      if (enabled) {
        this.lineEditingPlugin.settings.constrainDragToZAxis = false;
        this.setIsConstrainDragToZAxis(false);
      }
    });
  }

  /** Constrains drag handles to the X/Y values from drag start; mutually exclusive with {@link setConstrainDragZ}. */
  public setConstrainDragToZAxis(enabled: boolean): void {
    this.lineEditingPlugin.settings.constrainDragToZAxis = enabled;
    batch(() => {
      this.setIsConstrainDragToZAxis(enabled);
      if (enabled) {
        this.lineEditingPlugin.settings.constrainDragZ = false;
        this.setIsConstrainDragZ(false);
      }
    });
  }

  public getICADObjectsByGuid(guids: string[]): ICADObject[] {
    const guidSet = new Set(guids);
    const matches = this.collectEntitiesMatchingGuids(
      this.lineEditingModel,
      guidSet
    );
    for (const model of this.readonlyLineEditingModels.values()) {
      matches.push(...this.collectEntitiesMatchingGuids(model, guidSet));
    }
    return matches;
  }

  private collectEntitiesMatchingGuids(
    model: LineEditingModel | undefined,
    guidSet: ReadonlySet<string>
  ): ICADObject[] {
    if (!model) {
      return [];
    }
    return model.allEntities().filter((entity) => guidSet.has(entity.guid));
  }

  public getReadonlyLineEditingModels(): ReadonlyMap<string, LineEditingModel> {
    return this.readonlyLineEditingModels;
  }

  public setEditableModelSpaceId(modelSpaceId: string | undefined): void {
    this.editableModelSpaceId = modelSpaceId;
  }

  public getEditableModelSpaceId(): string | undefined {
    return this.editableModelSpaceId;
  }

  /**
   * True when `guid` belongs to a visible readonly drafting space. Uses the
   * last applied spec (survives plugin deletes) and live model contents.
   */
  public isReadonlyCadGuid(guid: string): boolean {
    return this.getReadonlyLineEditingModelsForGuids([guid]).length > 0;
  }

  /**
   * Readonly models that own any of `guids`. Uses the last-applied snapshot
   * (survives plugin deletes) and live model contents so a rejected edit can
   * undo only the space that was touched.
   */
  public getReadonlyLineEditingModelsForGuids(
    guids: readonly string[]
  ): LineEditingModel[] {
    const guidSet = new Set(guids.filter((guid) => guid.length > 0));
    if (guidSet.size === 0) {
      return [];
    }

    const models: LineEditingModel[] = [];
    for (const [modelSpaceId, model] of this.readonlyLineEditingModels) {
      if (this.readonlyModelOwnsAnyGuid(modelSpaceId, model, guidSet)) {
        models.push(model);
      }
    }
    return models;
  }

  private readonlyModelOwnsAnyGuid(
    modelSpaceId: string,
    model: LineEditingModel,
    guidSet: ReadonlySet<string>
  ): boolean {
    const remembered = this.readonlyCadGuidsByModelSpace.get(modelSpaceId);
    if (remembered !== undefined) {
      for (const guid of guidSet) {
        if (remembered.has(guid)) {
          return true;
        }
      }
    }
    return model.allEntities().some((entity) => guidSet.has(entity.guid));
  }

  public createReadonlyLineEditingModel(
    modelSpaceId: string
  ): LineEditingModel {
    const existing = this.readonlyLineEditingModels.get(modelSpaceId);
    if (existing) {
      return existing;
    }
    const modelId = DataPrepCapability.getReadonlyModelId(modelSpaceId);
    const model = this.lineEditingPlugin.createModel({ modelId });
    this.keepEditableTransformHandles(model);
    this.readonlyLineEditingModels.set(modelSpaceId, model);
    this.routeToolsToEditableModel();
    return model;
  }

  /**
   * The viewer broadcasts every selection change to all loaded models, and all line-editing models
   * share the plugin's single transform tool. A readonly model clearing its selection therefore
   * detaches the handles the editable model just attached, and readonly models are always created
   * after the editable one so they react last - leaving editable points without an elevation gizmo
   * whenever another drafting space was visible. Clearing only matters when the readonly model
   * actually holds a selection, and the editable handles are restored afterwards when it does.
   */
  private keepEditableTransformHandles(model: LineEditingModel): void {
    const clearReadonlySelection = model._clearSelection.bind(model);
    model._clearSelection = (): void => {
      if (model.getSelectedEntities().length === 0) {
        return;
      }
      clearReadonlySelection();
      this.reapplyEditableSelection();
    };
  }

  /**
   * Re-runs the editable model's own selection so its transform handles are re-attached. Goes
   * through the model instead of `viewer.selection` on purpose: writing to the viewer selection
   * broadcasts another change, which would clobber the handles again.
   */
  private reapplyEditableSelection(): void {
    const editableModel = this.lineEditingModel;
    if (!editableModel) {
      return;
    }
    const selectedIds = editableModel
      .getSelectedEntities()
      .map((entity) => entity.id);
    if (selectedIds.length === 0) {
      return;
    }
    editableModel._setSelection(selectedIds);
  }

  public setReadonlySurfaceGuids(
    modelSpaceId: string,
    guids: ReadonlySet<string>
  ): void {
    if (guids.size === 0) {
      this.readonlySurfaceGuidsByModelSpace.delete(modelSpaceId);
      return;
    }
    this.readonlySurfaceGuidsByModelSpace.set(modelSpaceId, guids);
  }

  private snapshotReadonlyCadGuids(model: LineEditingModel): void {
    const modelSpaceId = this.findReadonlyModelSpaceId(model);
    if (modelSpaceId === undefined) {
      return;
    }
    const guids = new Set(model.allEntities().map((entity) => entity.guid));
    if (guids.size === 0) {
      this.readonlyCadGuidsByModelSpace.delete(modelSpaceId);
      return;
    }
    this.readonlyCadGuidsByModelSpace.set(modelSpaceId, guids);
  }

  private findReadonlyModelSpaceId(
    model: LineEditingModel
  ): string | undefined {
    for (const [modelSpaceId, readonlyModel] of this
      .readonlyLineEditingModels) {
      if (readonlyModel === model) {
        return modelSpaceId;
      }
    }
    return undefined;
  }

  public async disposeReadonlyLineEditingModel(
    modelSpaceId: string
  ): Promise<void> {
    await this.textEditingModel?.removeSpace(modelSpaceId);

    const model = this.readonlyLineEditingModels.get(modelSpaceId);
    if (!model) {
      return;
    }

    const modelId = DataPrepCapability.getReadonlyModelId(modelSpaceId);
    this.readonlyLineEditingModels.delete(modelSpaceId);
    this.readonlySurfaceGuidsByModelSpace.delete(modelSpaceId);
    this.readonlyCadGuidsByModelSpace.delete(modelSpaceId);
    try {
      await this.viewer.unload(modelId, { fitToView: false });
    } catch (error: unknown) {
      console.error('Error unloading readonly model space model:', error);
    } finally {
      this.routeToolsToEditableModel();
    }
  }

  /**
   * `LineEditingPlugin.createModel` makes the new model the plugin's active model, which repoints
   * every drawing tool and the keyboard handler at it. Readonly model spaces must never capture
   * edits, so tool routing is restored to the editable model.
   */
  private routeToolsToEditableModel(): void {
    if (this.lineEditingModel) {
      this.lineEditingPlugin.setActiveModel(this.lineEditingModel);
    }
  }

  public static getReadonlyModelId(modelSpaceId: string): string {
    return `data-prep-readonly-${modelSpaceId}`;
  }

  public static isReadonlyModelId(modelId: string): boolean {
    return modelId.startsWith('data-prep-readonly-');
  }

  private handleActionPerformed(event: Event): void {
    const action = event as CustomEvent<LineEditingAction>;
    if (this.onActionPerformed) {
      this.onActionPerformed(action.detail);
    }
  }

  /**
   * Builds a scene-space polyline from a {@link CompoundCurve}. Returns an
   * empty array if the curve has fewer than 2 vertices.
   */
  private compoundCurveToPath(cc: CompoundCurve): Vector3[] {
    const bufferSize = cc.computeSize();
    const ccBuffer = new Float32Array(bufferSize);
    cc.generateGeometry(ccBuffer, new Vector3());
    const linePoints: Vector3[] = [];
    for (
      let i = 0;
      i + POSITION_FLOATS_PER_VERTEX <= ccBuffer.length;
      i += POSITION_FLOATS_PER_VERTEX
    ) {
      // eslint-disable-next-line no-magic-numbers
      const [x, y, z] = [ccBuffer[i]!, ccBuffer[i + 1]!, ccBuffer[i + 2]!];
      linePoints.push(new Vector3(x, y, z));
    }
    return linePoints;
  }

  /**
   * Re-publishes section paths for the currently-selected CAD entities. Used
   * both as the selection-change handler and as the sink's refresh hook
   * (for "toggle on with something already selected").
   */
  private publishSelectedSectionPaths(): void {
    const sink = this.lineSelectionSections;
    if (!sink) {
      return;
    }
    sink.clearSource(DATA_PREP_SECTION_SOURCE);
    const selectedCadIds = this.viewer.selection.get(DATA_PREP_MODEL_NAME);
    if (!Array.isArray(selectedCadIds) || selectedCadIds.length === 0) {
      return;
    }
    const selectedEntities = resolveSelectedEntities(
      this.lineEditingModel,
      selectedCadIds
    );
    for (const entity of selectedEntities) {
      if (entity instanceof CompoundCurve) {
        const path = this.compoundCurveToPath(entity);
        if (path.length >= MIN_PATH_VERTICES) {
          sink.setPath(DATA_PREP_SECTION_SOURCE, entity.guid, path);
        }
      }
    }
  }

  public handleSelection(): void {
    this.publishSelection();
  }

  private handleTextSelection(): void {
    this.publishSelection();
  }

  private handleTextInsertionPointChanged(event: Event): void {
    const detail = (
      event as CustomEvent<{
        guid: string;
        insertionPoint: { x: number; y: number; z: number };
      }>
    ).detail;
    if (!detail?.guid || !detail.insertionPoint) {
      return;
    }
    this.onTextInsertionPointChanged?.(detail);
  }

  private handleTextCreated(event: Event): void {
    const detail = (event as CustomEvent<{ text?: ITextObject }>).detail;
    if (!detail?.text?.guid) {
      return;
    }
    this.onTextCreated?.(detail.text);
  }

  private publishSelection(): void {
    const selectedThreeJsModelIds = this.viewer.selection.get(THREEJS_MODEL);
    const { entities: selectedEntities, hasReadonlyCadSelection } =
      this.collectSelectedCadEntities();

    this.publishSelectedSectionPaths();

    let selectedModels: Object3D<Object3DEventMap>[] = [];
    if (selectedThreeJsModelIds && selectedThreeJsModelIds.length > 0) {
      selectedModels =
        selectedThreeJsModelIds
          .map((x) => this.threeJsModel?.root.getObjectById(x))
          .filter((x) => x !== undefined) ?? [];
    }

    const hasReadonlyTextSelection =
      this.textEditingModel
        ?.getSelectedEntities()
        .some((entity) => !entity.editable) ?? false;

    const selectionIsReadonly =
      hasReadonlyCadSelection ||
      hasReadonlyTextSelection ||
      selectedModels.some((model) => this.isReadonlySurfaceMesh(model));

    if (this.onSelected) {
      this.onSelected(
        selectedEntities,
        selectedModels,
        selectionIsReadonly,
        this.textEditingModel?.getSelectedEntities()
      );
    }
  }

  /**
   * Collects CAD from the editable model and every visible readonly model. Mixed
   * selections used to drop the readonly entities, which made mutate actions
   * appear available for readonly geometry.
   */
  private collectSelectedCadEntities(): {
    entities: CADEntity[];
    hasReadonlyCadSelection: boolean;
  } {
    const selectedCadIds = this.viewer.selection.get(DATA_PREP_MODEL_NAME);
    const entities: CADEntity[] = [];
    let hasReadonlyCadSelection = false;

    if (Array.isArray(selectedCadIds) && selectedCadIds.length > 0) {
      entities.push(
        ...resolveSelectedEntities(this.lineEditingModel, selectedCadIds)
      );
    }

    const readonlyModels = this.readonlyLineEditingModels;
    for (const [modelSpaceId, readonlyModel] of readonlyModels) {
      const modelId = DataPrepCapability.getReadonlyModelId(modelSpaceId);
      const readonlyCadIds = this.viewer.selection.get(modelId);
      if (!Array.isArray(readonlyCadIds) || readonlyCadIds.length === 0) {
        continue;
      }
      const readonlyEntities = resolveSelectedEntities(
        readonlyModel,
        readonlyCadIds
      );
      if (readonlyEntities.length === 0) {
        continue;
      }
      entities.push(...readonlyEntities);
      hasReadonlyCadSelection = true;
    }

    return { entities, hasReadonlyCadSelection };
  }

  /**
   * Takes surface meshes that have left the scene out of the viewer's selection. Removing a mesh
   * does not touch the selection, and the viewer holds on to the id: it keeps drawing the outline
   * it cloned when the surface was picked, and its next selection pass dereferences the missing
   * object and throws before any model gets to update its own selection - which is what left the
   * viewer unable to select anything after another user deleted a selected surface.
   */
  public dropRemovedSurfacesFromSelection(): void {
    const root = this.threeJsModel?.root;
    const selectedIds = this.viewer.selection.get(THREEJS_MODEL);
    if (!root || !selectedIds || selectedIds.length === 0) {
      return;
    }

    const removedIds = selectedIds.filter(
      (id) => root.getObjectById(id) === undefined
    );
    if (removedIds.length > 0) {
      this.viewer.selection.subtract(THREEJS_MODEL, removedIds, this);
    }
  }

  /**
   * Applies a line rendering spec to the plugin.
   * - The full spec lines/points are passed through (including pending guids) so styles stay in
   *   sync when layer visibility toggles (hidden styling vs restored layer style). The plugin
   *   diffs the spec against its state and applies changes (including styles) in place, so
   *   unchanged entities are untouched and entity identity is preserved.
   * - keepObject keeps entities that are in presentIds/pendingGuids/site-boundary; everything
   *   else was removed on the server and is cleaned out of the model.
   */
  public async setLineRenderingSpecification(
    specification: LineRenderingSpecification
  ): Promise<void> {
    if (!this.lineEditingModel) {
      console.warn('Setting line rendering spec before model is ready');
      return;
    }
    await this.applyLineRenderingSpecification(
      this.lineEditingModel,
      specification
    );
  }

  /**
   * Every line-editing model shares the plugin's settings dispatcher, and `viewOnlyMode` decides
   * both whether upserted geometry is editable and whether tools accept input. Applies are
   * therefore serialized: overlapping editable/readonly applies would otherwise mark editable
   * geometry as view-only and make tools (including delete) silently no-op.
   */
  public async applyLineRenderingSpecification(
    model: LineEditingModel,
    specification: LineRenderingSpecification
  ): Promise<void> {
    const apply = this.lineRenderingApplyQueue.then(() =>
      this.applyLineRenderingSpecificationInternal(model, specification)
    );
    this.lineRenderingApplyQueue = apply.then(
      () => undefined,
      () => undefined
    );
    await apply;
  }

  private async applyLineRenderingSpecificationInternal(
    model: LineEditingModel,
    specification: LineRenderingSpecification
  ): Promise<void> {
    const settingsDispatcher = model.settingsDispatcher;
    const isEditableTarget = model === this.lineEditingModel;

    // Line-editing models share one plugin settings dispatcher; viewOnlyMode must
    // only be enabled transiently while upserting readonly geometry.
    if (settingsDispatcher) {
      settingsDispatcher.set(
        settingsDispatcher.settings,
        'viewOnlyMode',
        !isEditableTarget
      );
    }

    const selectedEntities = model.getSelectedEntities();
    const lines = specification.lines;
    const points = specification.points;

    try {
      await model.upsertAndClean(lines, points, (object) => {
        const appId = object.metaData['appId'] as number | undefined;
        const guid =
          (object as { guid?: string }).guid ??
          (object.metaData['guid'] as string | undefined);
        return (
          specification.presentIds.includes(appId ?? 0) ||
          (guid !== undefined && specification.pendingGuids?.has(guid)) ||
          object.metaData['type'] === 'site-boundary'
        );
      });
      if (!isEditableTarget) {
        this.snapshotReadonlyCadGuids(model);
      }
      // Restoring an empty selection is not a no-op: it publishes a selection change to the
      // viewer, which makes every other model re-run its own selection handling and drop the
      // editable model's transform handles. Once something is selected though, the change has to
      // be published even when nothing survived the apply - that is what takes the selection
      // outline off geometry another user just deleted.
      if (selectedEntities.length > 0) {
        this.restoreSelection(model, selectedEntities);
      }
    } finally {
      if (settingsDispatcher) {
        settingsDispatcher.set(
          settingsDispatcher.settings,
          'viewOnlyMode',
          false
        );
      }
    }

    if (this.textEditingModel) {
      const selectedTextEntities = this.textEditingModel.getSelectedEntities();
      const readonlyModelSpaceId = isEditableTarget
        ? undefined
        : this.findReadonlyModelSpaceId(model);
      const modelSpaceId = readonlyModelSpaceId ?? this.editableModelSpaceId;
      if (modelSpaceId === undefined) {
        return;
      }

      const isReadonlyOverlay = readonlyModelSpaceId !== undefined;
      const texts = (specification.texts ?? []).map((text) => ({
        ...text,
        modelSpaceId,
        editable: !isReadonlyOverlay,
      }));

      const incomingTextGuids = new Set(texts.map((text) => text.guid));
      await this.textEditingModel.upsertSpaceTexts(
        modelSpaceId,
        texts,
        (object) => {
          const guid = object.guid ?? object.metaData.guid;
          if (guid !== undefined && incomingTextGuids.has(guid)) {
            return true;
          }
          const appId = object.metaData.appId;
          return (
            specification.presentIds.includes(appId ?? 0) ||
            (guid !== undefined && specification.pendingGuids?.has(guid)) ||
            object.metaData.type === 'site-boundary'
          );
        }
      );
      if (selectedTextEntities.length > 0) {
        const previousGuids = selectedTextEntities.map((entity) => entity.guid);
        const remainingSelectedGuids = previousGuids.filter((guid) =>
          this.textEditingModel?.getEntityByGuid(guid)
        );
        if (remainingSelectedGuids.length !== previousGuids.length) {
          this.textEditingModel.setSelectedEntities(remainingSelectedGuids);
          this.publishSelection();
        }
      }
    }
  }

  private getTextDragConstraintSettings(): TextDragConstraintSettings {
    return {
      constrainDragZ: this.lineEditingPlugin.settings.constrainDragZ === true,
      constrainDragToZAxis:
        this.lineEditingPlugin.settings.constrainDragToZAxis === true,
    };
  }

  /**
   * Re-selects what is left of a selection captured before geometry was applied. Best-effort on
   * purpose: the geometry is already in the model by this point, and letting a selection failure
   * propagate would abort the apply, which in turn aborts a readonly resync and leaves surfaces
   * and layers stale.
   */
  private restoreSelection(
    model: LineEditingModel,
    selectedEntities: CADEntity[]
  ): void {
    try {
      model.setSelectedEntities(selectionStillInModel(model, selectedEntities));
    } catch (error) {
      console.warn('Restoring the selection after an apply failed', error);
    }
  }

  public setDrawingStyle(style: CADStyle): void {
    this.lineEditingModel?.setDrawingStyle(style);
  }

  /**
   * True when the given drafting space has geometry that {@link fitToView} can
   * frame: CAD entities and/or surface meshes belonging to that space only.
   */
  public hasGeometryToFit(modelSpaceId: string, isEditable: boolean): boolean {
    return this.collectModelsToFit(modelSpaceId, isEditable).size > 0;
  }

  /**
   * Fits the camera to geometry of a single drafting space (CAD and surfaces),
   * ignoring other visible editable or readonly spaces.
   */
  public async fitToView(
    modelSpaceId: string,
    isEditable: boolean
  ): Promise<void> {
    const models = this.collectModelsToFit(modelSpaceId, isEditable);
    if (models.size === 0) {
      return;
    }

    try {
      await this.viewer.camera.fitToView({ models });
    } catch (error: unknown) {
      console.error('Failed to fit data prep models to view', error);
      throw error;
    }
  }

  private collectModelsToFit(
    modelSpaceId: string,
    isEditable: boolean
  ): Map<string, number[]> {
    const models = new Map<string, number[]>();

    if (isEditable) {
      if ((this.lineEditingModel?.allEntities().length ?? 0) > 0) {
        models.set(DATA_PREP_MODEL_NAME, []);
      }
      const editableSurfaceIds = this.getEditableSurfaceObjectIds();
      const textMeshIds = this.textEditingModel?.getTextMeshObjectIds() ?? [];
      const threejsIds = [...editableSurfaceIds, ...textMeshIds];
      if (threejsIds.length > 0) {
        models.set(THREEJS_MODEL, threejsIds);
      }
      return models;
    }

    const readonlyCad = this.readonlyLineEditingModels.get(modelSpaceId);
    if ((readonlyCad?.allEntities().length ?? 0) > 0) {
      models.set(DataPrepCapability.getReadonlyModelId(modelSpaceId), []);
    }
    const readonlySurfaceIds = this.getSurfaceObjectIds(
      this.readonlySurfaceGuidsByModelSpace.get(modelSpaceId)
    );
    if (readonlySurfaceIds.length > 0) {
      models.set(THREEJS_MODEL, readonlySurfaceIds);
    }
    return models;
  }

  private getSurfaceObjectIds(
    guids: ReadonlySet<string> | undefined
  ): number[] {
    if (!guids || guids.size === 0 || !this.threeJsModel) {
      return [];
    }
    const ids: number[] = [];
    for (const guid of guids) {
      const object = this.threeJsModel.root?.getObjectByProperty('uuid', guid);
      if (object && typeof object.id === 'number') {
        ids.push(object.id);
      }
    }
    return ids;
  }

  private getEditableSurfaceObjectIds(): number[] {
    const children = this.threeJsModel?.root?.children;
    if (!children) {
      return [];
    }
    const textGroupIds = new Set(
      this.textEditingModel?.getOwnedGroupIds() ?? []
    );
    return children
      .filter(
        (child) =>
          !this.isReadonlySurfaceMesh(child) && !textGroupIds.has(child.id)
      )
      .map((child) => child.id);
  }

  /**
   * Unloads all models managed by the extension.
   * @throws Error if the active model is not initialized.
   */
  private unload(): void {
    Object.keys(this.mapModels).forEach((key) => {
      this.viewer
        .unload(this.mapModels[key].modelId)
        .catch((error: unknown) => {
          console.error('Error unloading model:', error);
        });
    });
  }

  private isReadonlySurfaceMesh(object: Object3D<Object3DEventMap>): boolean {
    for (const guids of this.readonlySurfaceGuidsByModelSpace.values()) {
      if (guids.has(object.uuid)) {
        return true;
      }
    }
    return false;
  }

  private handlePick(e: Event): void {
    const event = e as CustomEvent<PickEventDetail>;
    if (event.detail.modelId === 'icons') {
      const iconId = event.detail.id;
      console.log('Icon picked:', iconId);
    }
  }
}
